import { Link2, QrCode, BarChart3 } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { Card } from '@/components/ui';

const Features = () => {
  const { t } = useTranslation();

  const features = [
    {
      icon: <Link2 className="w-6 h-6" />,
      title: t('features.smartDeeplinks'),
      subtitle: t('features.smartDeeplinksSubtitle'),
      description: t('features.smartDeeplinksDescription'),
      color: 'bg-lime-400',
      textColor: 'text-dark-800',
    },
    {
      icon: <QrCode className="w-6 h-6" />,
      title: t('features.customQRCodes'),
      subtitle: t('features.customQRCodesSubtitle'),
      description: t('features.customQRCodesDescription'),
      color: 'bg-lavender-400',
      textColor: 'text-dark-800',
    },
    {
      icon: <BarChart3 className="w-6 h-6" />,
      title: t('features.detailedAnalytics'),
      subtitle: t('features.detailedAnalyticsSubtitle'),
      description: t('features.detailedAnalyticsDescription'),
      color: 'bg-dark-800',
      textColor: 'text-white',
    },
  ];

  return (
    <section id="features" className="py-24 bg-white dark:bg-dark-900">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="text-center max-w-3xl mx-auto mb-16">
          <h2 className="text-4xl sm:text-5xl font-bold text-dark-800 dark:text-white mb-6">
            {t('features.sectionTitle')}{' '}
            <span className="text-lime-500">{t('features.sectionTitleHighlight')}</span>
          </h2>
          <p className="text-xl text-gray-600 dark:text-gray-400">
            {t('features.sectionSubtitle')}
          </p>
        </div>

        {/* Features Grid */}
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
          {features.map((feature, index) => (
            <Card
              key={index}
              variant="interactive"
              padding="lg"
              className="group"
            >
              <div className={`w-14 h-14 ${feature.color} rounded-2xl flex items-center justify-center ${feature.textColor} mb-6 group-hover:scale-110 transition-transform duration-300`}>
                {feature.icon}
              </div>
              <h3 className="text-xl font-semibold text-dark-800 dark:text-white mb-1">
                {feature.title}
              </h3>
              <p className="text-sm text-gray-500 dark:text-gray-400 mb-3">{feature.subtitle}</p>
              <p className="text-gray-600 dark:text-gray-400">
                {feature.description}
              </p>
            </Card>
          ))}
        </div>

        {/* Platforms Showcase */}
        <div className="mt-24 text-center">
          <p className="text-sm font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider mb-8">
            {t('features.platformsShowcase')}
          </p>
          <div className="flex flex-wrap items-center justify-center gap-8 opacity-60 dark:text-gray-400">
            {/* Spotify */}
            <svg className="h-8 w-auto" viewBox="0 0 168 168" fill="currentColor">
              <path d="M84 0C37.6 0 0 37.6 0 84s37.6 84 84 84 84-37.6 84-84S130.4 0 84 0zm38.5 121.2c-1.5 2.5-4.7 3.2-7.1 1.7-19.5-11.9-44-14.6-72.9-8-2.8.6-5.6-1.1-6.2-3.9-.6-2.8 1.1-5.6 3.9-6.2 31.6-7.2 58.7-4.1 80.6 9.3 2.4 1.5 3.2 4.7 1.7 7.1zm10.3-22.8c-1.9 3.1-5.9 4-8.9 2.1-22.3-13.7-56.3-17.7-82.7-9.7-3.4 1-7-1-8-4.4-1-3.4 1-7 4.4-8 30.2-9.1 67.8-4.7 93.2 11.1 3 1.9 3.9 5.8 2 8.9zm.9-23.8c-26.8-15.9-71-17.4-96.6-9.6-4.1 1.2-8.4-1.1-9.6-5.2-1.2-4.1 1.1-8.4 5.2-9.6 29.4-8.9 78.3-7.2 109.2 11.1 3.7 2.2 4.9 7 2.7 10.7-2.2 3.6-7 4.8-10.9 2.6z" />
            </svg>
            {/* YouTube */}
            <svg className="h-6 w-auto" viewBox="0 0 159 110" fill="currentColor">
              <path d="M154 17.5c-1.8-6.7-7.1-12-13.8-13.8C128 0 79.5 0 79.5 0S31 0 18.8 3.7C12.1 5.5 6.8 10.8 5 17.5 1.2 29.7 1.2 55 1.2 55s0 25.3 3.8 37.5c1.8 6.7 7.1 12 13.8 13.8 12.2 3.7 60.7 3.7 60.7 3.7s48.5 0 60.7-3.7c6.7-1.8 12-7.1 13.8-13.8 3.8-12.2 3.8-37.5 3.8-37.5s0-25.3-3.8-37.5zM64 79V32l40.5 23.5L64 79z" />
            </svg>
            {/* TikTok */}
            <svg className="h-7 w-auto" viewBox="0 0 448 512" fill="currentColor">
              <path d="M448,209.91a210.06,210.06,0,0,1-122.77-39.25V349.38A162.55,162.55,0,1,1,185,188.31V278.2a74.62,74.62,0,1,0,52.23,71.18V0l88,0a121.18,121.18,0,0,0,1.86,22.17h0A122.18,122.18,0,0,0,381,102.39a121.43,121.43,0,0,0,67,20.14Z" />
            </svg>
            {/* Amazon */}
            <svg className="h-7 w-auto" viewBox="0 0 448 512" fill="currentColor">
              <path d="M257.2 162.7c-48.7 1.8-169.5 15.5-169.5 117.5 0 109.5 138.3 114 183.5 43.2 6.5 10.2 35.4 37.5 45.3 46.8l56.8-56S341 288.9 341 261.4V114.3C341 89 316.5 32 228.7 32 140.7 32 94 87 94 136.3l73.5 6.8c16.3-49.5 54.2-49.5 54.2-49.5 40.7-.1 35.5 29.8 35.5 69.1zm0 86.8c0 80-84.2 68-84.2 17.2 0-47.2 50.5-56.7 84.2-57.8v40.6zm136 163.5c-7.7 10-70 67-174.5 67S34.2 408.5 9.7 379c-6.8-7.7 1-11.3 5.5-8.3C88.5 415.2 203 488.5 387.7 401c7.5-3.7 13.3 2 5.5 12zm39.8 2.2c-6.5 15.8-16 26.8-21.2 31-5.5 4.5-9.5 2.7-6.5-3.8s19.3-46.5 12.7-55c-6.5-8.3-37-4.3-48-3.2-10.8 1-13 2-14-.3-2.3-5.7 21.7-15.5 37.5-17.5 15.7-1.8 41-.8 46 5.7 3.7 5.1 0 27.1-6.5 43.1z" />
            </svg>
            {/* Instagram */}
            <svg className="h-7 w-auto" viewBox="0 0 448 512" fill="currentColor">
              <path d="M224.1 141c-63.6 0-114.9 51.3-114.9 114.9s51.3 114.9 114.9 114.9S k339 319.5 339 255.9 287.7 141 224.1 141zm0 189.6c-41.1 0-74.7-33.5-74.7-74.7s33.5-74.7 74.7-74.7 74.7 33.5 74.7 74.7-33.6 74.7-74.7 74.7zm146.4-194.3c0 14.9-12 26.8-26.8 26.8-14.9 0-26.8-12-26.8-26.8s12-26.8 26.8-26.8 26.8 12 26.8 26.8zm76.1 27.2c-1.7-35.9-9.9-67.7-36.2-93.9-26.2-26.2-58-34.4-93.9-36.2-37-2.1-147.9-2.1-184.9 0-35.8 1.7-67.6 9.9-93.9 36.1s-34.4 58-36.2 93.9c-2.1 37-2.1 147.9 0 184.9 1.7 35.9 9.9 67.7 36.2 93.9s58 34.4 93.9 36.2c37 2.1 147.9 2.1 184.9 0 35.9-1.7 67.7-9.9 93.9-36.2 26.2-26.2 34.4-58 36.2-93.9 2.1-37 2.1-147.8 0-184.8zM398.8 388c-7.8 19.6-22.9 34.7-42.6 42.6-29.5 11.7-99.5 9-132.1 9s-102.7 2.6-132.1-9c-19.6-7.8-34.7-22.9-42.6-42.6-11.7-29.5-9-99.5-9-132.1s-2.6-102.7 9-132.1c7.8-19.6 22.9-34.7 42.6-42.6 29.5-11.7 99.5-9 132.1-9s102.7-2.6 132.1 9c19.6 7.8 34.7 22.9 42.6 42.6 11.7 29.5 9 99.5 9 132.1s2.7 102.7-9 132.1z" />
            </svg>
            {/* Apple Music */}
            <svg className="h-7 w-auto" viewBox="0 0 384 512" fill="currentColor">
              <path d="M318.7 268.7c-.2-36.7 16.4-64.4 50-84.8-18.8-26.9-47.2-41.7-84.7-44.6-35.5-2.8-74.3 20.7-88.5 20.7-15 0-49.4-19.7-76.4-19.7C63.3 141.2 4 184.8 4 273.5q0 39.3 14.4 81.2c12.8 36.7 59 126.7 107.2 125.2 25.2-.6 43-17.9 75.8-17.9 31.8 0 48.3 17.9 76.4 17.9 48.6-.7 90.4-82.5 102.6-119.3-65.2-30.7-61.7-90-61.7-91.9zm-56.6-164.2c27.3-32.4 24.8-61.9 24-72.5-24.1 1.4-52 16.4-67.9 34.9-17.5 19.8-27.8 44.3-25.6 71.9 26.1 2 49.9-11.4 69.5-34.3z" />
            </svg>
          </div>
        </div>
      </div>
    </section>
  );
};

export default Features;
